import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { AuthShell } from "@/pages/AuthShell";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/dashboard");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirect = `${window.location.origin}/dashboard`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <AuthShell
      title="Log in"
      subtitle="Access your statement workspace."
      footer={<>New here? <Link className="font-semibold text-primary" to="/signup" data-testid="to-signup-link">Create an account</Link></>}
    >
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                 placeholder="you@company.com" data-testid="login-email-input" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" required value={password}
                 onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
        </div>
        {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">{error}</p>}
        <Button type="submit" className="w-full rounded-full" disabled={busy} data-testid="login-submit-btn">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Log in
        </Button>
        <div className="flex items-center justify-between text-sm">
          <Link to="/forgot-password" className="text-primary hover:underline" data-testid="forgot-password-link">
            Forgot password?
          </Link>
        </div>
        <div className="relative py-2 text-center text-xs uppercase tracking-[0.2em] text-muted-foreground">
          <span className="bg-background px-3">or</span>
        </div>
        <Button type="button" variant="outline" className="w-full rounded-full bg-white" onClick={google}
                data-testid="google-login-btn">
          Continue with Google
        </Button>
      </form>
    </AuthShell>
  );
}
